import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import * as api from '../services/api';

// Define types
export interface Character {
  id: string;
  userId: string;
  name: string;
  type: 'Phantom' | 'Dark Cleric';
  level: number;
  experience: number;
  health: number;
  strength: number;
  defense: number;
  agility: number;
  intelligence: number;
  createdAt: string;
  lastPlayed?: string;
  transform?: any;
  healthData?: any;
  stateData?: any;
  inventoryData?: any;
  combatSkillsData?: any;
  currentLevel?: string;
}

export interface UserProfile {
  id: string;
  username: string;
  email: string;
  registeredAt: string;
  lastLogin?: string;
  isActive: boolean;
  currentLevel?: string;
  xp: number;
  valorPoints: number;
  lastXpUpdate?: string;
  lastValorUpdate?: string;
}

interface UserContextType {
  userProfile: UserProfile | null;
  characters: Character[];
  selectedCharacter: Character | null;
  isLoading: boolean;
  error: string | null;
  fetchUserData: () => Promise<void>;
  fetchCharacters: () => Promise<void>;
  selectCharacter: (character: Character) => void;
  createNewCharacter: (characterData: { name: string; type: 'Phantom' | 'Dark Cleric' }) => Promise<Character>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user data when user is authenticated
  const fetchUserData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const userData = await api.getUserProfile();
      setUserProfile(userData.data);
    } catch (err) {
      console.error("Error fetching user profile:", err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user profile');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch user characters
  const fetchCharacters = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.getUserCharacters();
      console.log('Raw characters response:', response);
      
      // Handle different response formats
      if (response && typeof response === 'object') {
        // Handle response with status and data fields (API format)
        if ('status' in response && 'data' in response && Array.isArray(response.data)) {
          console.log('Setting characters from response.data:', response.data);
          setCharacters(response.data || []);
          
          // If we have characters and none is selected, select the first one
          if (response.data && response.data.length > 0 && !selectedCharacter) {
            setSelectedCharacter(response.data[0]);
          }
        } 
        // Handle direct array response
        else if (Array.isArray(response)) {
          console.log('Setting characters from direct array:', response);
          setCharacters(response);
          
          // If we have characters and none is selected, select the first one
          if (response.length > 0 && !selectedCharacter) {
            setSelectedCharacter(response[0]);
          }
        }
        // Handle unexpected response format
        else {
          console.error('Unexpected characters response format:', response);
          setCharacters([]);
        }
      } else {
        console.error('Invalid response format from API:', response);
        setCharacters([]);
      }
    } catch (err) {
      console.error("Error fetching characters:", err);
      setError(err instanceof Error ? err.message : 'Failed to fetch characters');
      // Set empty array on error to ensure modal shows up
      setCharacters([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new character
  const createNewCharacter = async (characterData: { name: string; type: 'Phantom' | 'Dark Cleric' }) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const newCharacter = await api.createCharacter(characterData);
      setCharacters(prevCharacters => [...prevCharacters, newCharacter]);
      return newCharacter;
    } catch (err) {
      console.error("Error creating character:", err);
      setError(err instanceof Error ? err.message : 'Failed to create character');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Select a character
  const selectCharacter = (character: Character) => {
    setSelectedCharacter(character);
  };

  // Reset state when user changes
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      setCharacters([]);
      setSelectedCharacter(null);
    }
  }, [user]);

  const value = {
    userProfile,
    characters,
    selectedCharacter,
    isLoading,
    error,
    fetchUserData,
    fetchCharacters,
    selectCharacter,
    createNewCharacter
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}; 